import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { ChevronDown } from 'lucide-react';
// custom
import { ROUTES } from '@app/router/routes';
import NavigationMenu from './NavigationMenu';
import { useFontToggle } from '../hooks/useFontToggle';
import { logout } from '@/features/auth/api/authService';
import showVersion from '@/app/version';
import { useLanguage } from '@shared/contexts/LanguageContext';


export function onMouseDownVer(e) {
  if (e.button === 1) {
    const textVer = showVersion(false)
    copyToClipboard(textVer)
  }
}
async function copyToClipboard(text, tFn) {
  const successMsg = () => {
    const msg = tFn ? tFn('header.copiedToClipboard').replace('{{text}}', text) : `已複製到剪貼簿: \n${text}`;
    toast.success(msg);
  };
  const failMsg = (err) => {
    const msg = tFn ? tFn('header.copyFailed').replace('{{error}}', err) : `複製失敗: ${err}`;
    toast.error(msg);
  };

  if (navigator.clipboard && document.hasFocus()) {
    try {
      await navigator.clipboard.writeText(text);
      console.log('OK To Clipboard:', text);
      successMsg();
      return;
    } catch (err) {
      console.warn('Clipboard API failed, trying fallback:', err);
    }
  }

  // fallback for when document is not focused (e.g. middle-click)
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    console.log('OK To Clipboard (fallback):', text);
    successMsg();
  } catch (err) {
    console.error('Error To Clipboard:', err);
    failMsg(err);
  }
}

/**
 * 可配置的頁面頭部組件
 * 支援響應式設計（手機/平板/桌面）
 * 功能：
 * - Home 按鈕導向首頁
 * - 搜尋 Bar
 * - 字體與語言整合選單（圓體/明體，未來可擴展英文）
 * - Burger Menu 顯示導航選單
 *
 * @param {Object} props
 * @param {Object} props.user - 用戶資訊 { name, avatar }
 * @param {boolean} props.showHome - 是否顯示首頁按鈕
 * @param {Function} props.onSearchChange - 搜尋輸入變化回調
 * @param {string} props.className - 自定義 class
 */
//---------------------------------------------------------------------------
const PageHeader = ({
  user = null,
  showHome = true,
  onSearchChange = null,
  className = '',
  subMenu = null,
}) => {
  const navigate = useNavigate();
  useFontToggle(); // 套用字體到 body（目前強制明體）
  const { currentLanguage, setLanguage, availableLanguages, t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const scrollStopTimerRef = useRef(null);

  useEffect(() => {
    lastScrollYRef.current = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - lastScrollYRef.current;
      const directionThreshold = 4;

      if (currentScrollY <= 0) {
        // 在頁面頂端時保持顯示
        setIsHeaderVisible(true);
      } else if (scrollDelta > directionThreshold) {
        // 往下捲動時隱藏
        setIsHeaderVisible(false);
      } else if (scrollDelta < -directionThreshold) {
        // 往上捲動時顯示
        setIsHeaderVisible(true);
      }

      if (scrollStopTimerRef.current) {
        clearTimeout(scrollStopTimerRef.current);
      }

      // 停止捲動時隱藏
      /*
      scrollStopTimerRef.current = setTimeout(() => {
        if (window.scrollY > 0) {
          setIsHeaderVisible(false);
        }
      }, 120);
      */

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollStopTimerRef.current) {
        clearTimeout(scrollStopTimerRef.current);
      }
    };
  }, []);

  // Home 按鈕點擊
  const handleHomeClick = () => {
    navigate(ROUTES.HOME);
  };

  // 搜尋輸入變化
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };


  // Burger Menu 點擊
  const handleMenuClick = () => {
    setIsMenuOpen(true);
  };

  // 關閉選單
  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };
  
  const Divider = () => {
    return (
      <div className="w-full h-[1px] bg-stone-200"></div>
    );
  };
  const UserButton = ({ children, icon, ...props }) => {
    return (
      <button
        className="w-full text-left px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-primary transition-colors flex items-center gap-2"
        {...props}
      >
        {icon && <span className="material-symbols-outlined text-lg">{icon}</span>}
        {children}
      </button>
    );
  };
  
  //---------------------------------------------------------------------------
  return (
    <>
      <header className={`fixed top-0 w-full z-50 bg-white shadow-[0px_4px_8px_0px_rgba(0,0,0,0.20)] transition-transform duration-300 ${isHeaderVisible ? 'translate-y-0' : '-translate-y-full'} ${className}`}>
        <div className="w-full px-4 md:px-8 h-[70px] flex items-center justify-between gap-4">
          {/* 左側：Logo / 首頁按鈕 */}
          {showHome && (
            <button
              className="flex items-center justify-center h-14 flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
              onClick={handleHomeClick}
              onMouseDown={(e) => onMouseDownVer(e)}
              title={t('header.returnHomeTitle')}
            >
              <img src="/image/logo.png" alt={t('header.logoAlt') || t('nav.anniversary')} className="h-14 w-auto" />
            </button>
          )}

          {/* 中間：空白區域（搜尋已移到右側）*/}
          <div className="flex-1"></div>

          {/* 右側：字體切換、用戶名稱、搜尋、Burger Menu */}
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            {/* 用戶/訪客 下拉選單區域 */}
            <div className="relative">
              <button
                className="flex items-center gap-1 px-2 py-1 bg-stone-50 rounded-full border border-stone-100 hover:bg-stone-100 transition-colors cursor-pointer"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              >
                {user ? (
                  <>
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-5 h-5 rounded-full object-cover"
                      />
                    ) : (
                      <span className="material-symbols-outlined text-primary text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                        account_circle
                      </span>
                    )}
                    <span className="text-sm font-medium text-stone-700 whitespace-nowrap max-w-[80px] truncate">
                      {user.name}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-stone-400 text-xl">
                      account_circle
                    </span>
                    <span className="text-sm font-medium text-stone-500 whitespace-nowrap">
                      {t('header.guest')}
                    </span>
                  </>
                )}
                <ChevronDown className="text-stone-400 w-4 h-4" />
              </button>

              {/* 下拉選單 */}
              {isUserMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsUserMenuOpen(false)}
                  ></div>
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-[0px_4px_20px_0px_rgba(0,0,0,0.15)] ring-1 ring-black/5 z-50 overflow-hidden py-1 animate-scaleIn origin-top-right">
                    {user ? (
                      <>
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-sm text-stone-900 font-medium truncate">{user.name}</p>
                          <p className="text-xs text-stone-500 truncate">{t('header.loggedIn')}</p>
                        </div>
                        <UserButton icon="person"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            navigate(ROUTES.PROFILE);
                          }}
                        >
                          {t('header.profile')}
                        </UserButton>
                        <UserButton icon="qr_code"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            navigate(`${ROUTES.QR_CODE}?mode=qr`);
                          }}
                        >
                          {t('header.showQrCode')}
                        </UserButton>

                        <Divider  />

                        <UserButton icon="logout"
                          onClick={() => {
                            logout();
                            setIsUserMenuOpen(false);
                            navigate(ROUTES.HOME);
                            toast.success(t('header.logoutSuccess'));
                          }}
                        >
                          {t('header.logout')}
                        </UserButton>
                      </>
                    ) : (
                      <>
                        <UserButton icon="login"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            navigate(ROUTES.LOGIN);
                          }}
                        >
                          {t('header.login')}
                        </UserButton>

                        <UserButton icon="person_add"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            navigate(ROUTES.REGISTER);
                          }}
                        >
                          {t('header.register')}
                        </UserButton>

                        <Divider  />

                        <UserButton icon="qr_code"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            navigate(`${ROUTES.QR_CODE}?mode=qr`);
                          }}
                        >
                          {t('header.showQrCode')}
                        </UserButton>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* 搜尋按鈕（暫時隱藏） */}
            {/* <button
              className="w-9 h-9 flex items-center justify-center text-stone-500 bg-stone-50 rounded-full border border-stone-100 hover:bg-stone-100 transition-colors cursor-pointer"
              onClick={() => {
                console.log('搜尋按鈕被點擊');
              }}
              title="搜尋"
            >
              <span className="material-symbols-outlined text-xl">search</span>
            </button> */}

            {/* 語言切換 */}
            <div className="relative flex-shrink-0">
              <button
                className="flex items-center gap-1 px-2 py-1 bg-stone-50 rounded-full border border-stone-100 hover:bg-stone-100 transition-colors cursor-pointer select-none"
                onClick={() => setIsLangMenuOpen(v => !v)}
              >
                <span className="text-sm text-stone-600 whitespace-nowrap">{currentLanguage.shortLabel}</span>
                <span className="material-symbols-outlined text-base text-stone-500" style={{ fontSize: '18px' }}>language</span>
              </button>

              {isLangMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsLangMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-24 bg-white rounded-xl shadow-[0px_4px_20px_0px_rgba(0,0,0,0.15)] ring-1 ring-black/5 z-50 overflow-hidden py-1 animate-scaleIn origin-top-right">
                    {availableLanguages.map(lang => (
                      <button
                        key={lang.code}
                        className="w-full text-left px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                        onClick={() => { setLanguage(lang.code); setIsLangMenuOpen(false); }}
                      >
                        {lang.shortLabel}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Burger Menu 按鈕 */}
            <button
              className="w-9 h-9 flex items-center justify-center text-stone-500 bg-stone-50 rounded-full border border-stone-100 hover:bg-stone-100 transition-colors cursor-pointer"
              onClick={handleMenuClick}
              title={t('header.menuTitle')}
            >
              <span className="material-symbols-outlined text-xl">menu</span>
            </button>
          </div>
        </div>
        {/* subMenu slot — profile 頁積分條等 */}
        {subMenu && (
          <div className="w-full">
            {subMenu}
          </div>
        )}
      </header>

      {/* 導航選單 */}
      <NavigationMenu isOpen={isMenuOpen} onClose={handleCloseMenu} user={user} />
    </>
  );
};

PageHeader.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
    avatar: PropTypes.string,
  }),
  showHome: PropTypes.bool,
  onSearchChange: PropTypes.func,
  className: PropTypes.string,
};

export default PageHeader;
